import { PORT_FACES, VERIFIED_LABELS, VERIFIED_VALUES } from '../../types';
import type { EquipmentModelInput, EquipmentModelRow, PortFace, Verified } from '../../types';
import { VerifiedBadge } from '../InstallGuideBadge';
import { NumberField, Section, SelectField, TextField } from '../fields';

const FACE_OPTIONS = PORT_FACES.map((face) => ({ value: face, label: face === 'none' ? 'No port' : face[0].toUpperCase() + face.slice(1) }));
const VERIFIED_OPTIONS = VERIFIED_VALUES.map((value) => ({ value, label: VERIFIED_LABELS[value] }));

interface InstallGuideTabProps {
  model: EquipmentModelRow;
  canWrite: boolean;
  onSave: (data: Partial<EquipmentModelInput>) => void;
}

/**
 * Where ATEK installs on this model. Positions are fractions of the face/cabinet
 * (0 = left/bottom/front, 1 = right/top/back), as in the digital-assets drawings.
 */
export function EquipmentInstallGuideTab({ model, canWrite, onSave }: InstallGuideTabProps) {
  const disabled = !canWrite;
  const fraction = (id: string, label: string, key: keyof EquipmentModelInput) => (
    <NumberField id={id} label={label} value={model[key as keyof EquipmentModelRow] as number | null} min={0} max={1} step="0.01"
      disabled={disabled} onCommit={(value) => onSave({ [key]: value })} />
  );
  const verified = (id: string, key: 'portVerified' | 'probeVerified' | 'sensorVerified') => (
    <SelectField<Verified> id={id} label="Verified" value={model[key]} options={VERIFIED_OPTIONS} disabled={disabled}
      onChange={(value) => value && onSave({ [key]: value })} />
  );

  return (
    <div className="space-y-6">
      <Section label="Cable port" aside={<VerifiedBadge value={model.portVerified} testId="verified-port" />}>
        <div className="grid grid-cols-2 gap-3">
          <SelectField<PortFace> id="eq-port-face" label="Face" value={model.portFace} options={FACE_OPTIONS} allowEmpty disabled={disabled}
            onChange={(portFace) => onSave({ portFace })} />
          <NumberField id="eq-port-mm" label="Diameter (mm)" value={model.portMm} min={1} integer disabled={disabled}
            onCommit={(portMm) => onSave({ portMm })} />
          {fraction('eq-port-u', 'Port across (0–1)', 'portU')}
          {fraction('eq-port-v', 'Port up (0–1)', 'portV')}
          {verified('eq-port-verified', 'portVerified')}
        </div>
      </Section>

      <Section label="Probe" aside={<VerifiedBadge value={model.probeVerified} testId="verified-probe" />}>
        <div className="grid grid-cols-3 gap-3">
          {fraction('eq-probe-u', 'Probe across (0–1)', 'probeU')}
          {fraction('eq-probe-v', 'Probe up (0–1)', 'probeV')}
          {fraction('eq-probe-w', 'Probe depth (0–1)', 'probeW')}
        </div>
        <div className="grid grid-cols-2 gap-3">{verified('eq-probe-verified', 'probeVerified')}</div>
      </Section>

      <Section label="Sensor / transmitter" aside={<VerifiedBadge value={model.sensorVerified} testId="verified-sensor" />}>
        <div className="grid grid-cols-3 gap-3">
          {fraction('eq-sensor-u', 'Sensor across (0–1)', 'sensorU')}
          {fraction('eq-sensor-v', 'Sensor up (0–1)', 'sensorV')}
          {fraction('eq-sensor-w', 'Sensor depth (0–1)', 'sensorW')}
        </div>
        <div className="grid grid-cols-2 gap-3">{verified('eq-sensor-verified', 'sensorVerified')}</div>
        <TextField id="eq-sensor-source" label="How the sensor position was established" value={model.sensorSource} multiline disabled={disabled}
          onCommit={(sensorSource) => onSave({ sensorSource: sensorSource.trim() || null })} />
      </Section>

      <Section label="Notes for technicians">
        <TextField id="eq-install-notes" label="Shown in the technician portal" value={model.installNotes} multiline disabled={disabled}
          placeholder="e.g. Remove the rubber plug from the top-left access port before routing the cable."
          onCommit={(installNotes) => onSave({ installNotes: installNotes.trim() || null })} />
      </Section>
    </div>
  );
}
